import React, { useState, useEffect, useRef, useCallback } from "react";
import {
    Layout, Table, Input, Button, Form, Space, Row, Col,
    Breadcrumb, Modal, message, Typography, Tooltip, Switch
} from "antd";
import Headerdiv from "../../layout/header";
import Footerdiv from "../../layout/footer";
import {
    PlusOutlined, EditOutlined, HomeOutlined,
    SearchOutlined, CheckCircleFilled, CloseCircleFilled, ToolOutlined
} from "@ant-design/icons";
import api from "../../../services/axiosInstance";
import Highlighter from "react-highlight-words";
import "../../../styles/fluidDesign.css";

const { Content } = Layout;

const ManageMaintainCategory = () => {
    const [categories, setCategories]         = useState([]);
    const [loading, setLoading]               = useState(false);
    const [isModalVisible, setIsModalVisible] = useState(false);
    const [currentItem, setCurrentItem]       = useState(null);
    const [addForm]  = Form.useForm();
    const [editForm] = Form.useForm();

    const [pagination, setPagination] = useState({ current: 1, pageSize: 10, total: 0 });
    const [isAddModalVisible, setIsAddModalVisible] = useState(false);

    const [searchText, setSearchText]           = useState('');
    const [searchedColumn, setSearchedColumn]   = useState('');
    const searchInput = useRef(null);

    /* ─── column search helpers ─────────────────────────────── */
    const handleSearch = (selectedKeys, confirm, dataIndex) => {
        confirm(); setSearchText(selectedKeys[0]); setSearchedColumn(dataIndex);
    };
    const handleReset = (clearFilters) => { clearFilters(); setSearchText(''); };

    const getColumnSearchProps = (dataIndex) => ({
        filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters, close }) => (
            <div style={{ padding: 8 }} onKeyDown={(e) => e.stopPropagation()}>
                <Input
                    ref={searchInput}
                    placeholder={`Search ${dataIndex}`}
                    value={selectedKeys[0]}
                    onChange={(e) => setSelectedKeys(e.target.value ? [e.target.value] : [])}
                    onPressEnter={() => handleSearch(selectedKeys, confirm, dataIndex)}
                    style={{ marginBottom: 8, display: 'block' }}
                />
                <Space>
                    <Button type="primary" onClick={() => handleSearch(selectedKeys, confirm, dataIndex)}
                        icon={<SearchOutlined />} size="small" style={{ width: 90 }}>Search</Button>
                    <Button onClick={() => clearFilters && handleReset(clearFilters)} size="small" style={{ width: 90 }}>Reset</Button>
                    <Button type="link" size="small" onClick={() => { confirm({ closeDropdown: false }); setSearchText(selectedKeys[0]); setSearchedColumn(dataIndex); }}>Filter</Button>
                    <Button type="link" size="small" onClick={() => close()}>close</Button>
                </Space>
            </div>
        ),
        filterIcon: (filtered) => <SearchOutlined style={{ color: filtered ? '#1677ff' : undefined }} />,
        onFilter: (value, record) => record[dataIndex]?.toString().toLowerCase().includes(value.toLowerCase()),
        filterDropdownProps: { onOpenChange(open) { if (open) setTimeout(() => searchInput.current?.select(), 100); } },
        render: (text) => searchedColumn === dataIndex ? (
            <Highlighter
                highlightStyle={{ backgroundColor: '#ffc069', padding: 0 }}
                searchWords={[searchText]} autoEscape
                textToHighlight={text ? text.toString() : ''}
            />
        ) : text,
    });

    /* ─── fetch ──────────────────────────────────────────────── */
    const fetchCategories = useCallback(async (page = 1, pageSize = 10) => {
        try {
            setLoading(true);
            const res = await api.get(`/settings/maintaincategory/list.php?page=${page}&limit=${pageSize}`);
            setCategories(res.data.dataset.map(item => ({
                mcId:     item.mc_id,
                mcName:   item.mc_name,
                mcActive: item.mc_active,
            })));
            setPagination({
                current:  res.data.pagination?.current_page  || 1,
                pageSize: res.data.pagination?.per_page       || 10,
                total:    res.data.pagination?.total_records  || 0,
            });
        } catch {
            message.error("Failed to fetch maintenance categories.");
        } finally { setLoading(false); }
    }, []);

    useEffect(() => { fetchCategories(1, 10); }, [fetchCategories]);
    const handleTableChange = (pg) => fetchCategories(pg.current, pg.pageSize);

    /* ─── add ────────────────────────────────────────────────── */
    const handleAdd = async (values) => {
        try {
            setLoading(true);
            const res = await api.post('/settings/maintaincategory/add.php', { mc_name: values.mcName });
            if (res.data.status === 'success') {
                message.success(res.data.message || "Category added successfully.");
                setIsAddModalVisible(false);
                fetchCategories(pagination.current, pagination.pageSize);
                addForm.resetFields();
            } else throw new Error(res.data.message);
        } catch (err) {
            message.error(err?.response?.data?.message || "Failed to add category.");
        } finally { setLoading(false); }
    };

    /* ─── edit modal ─────────────────────────────────────────── */
    const openEdit = (record) => {
        setCurrentItem(record);
        editForm.setFieldsValue({ mcName: record.mcName });
        setIsModalVisible(true);
    };

    const handleEdit = async (values) => {
        try {
            setLoading(true);
            const res = await api.post('/settings/maintaincategory/edit.php', {
                mc_id:   currentItem.mcId,
                mc_name: values.mcName,
            });
            if (res.data.status === 'success') {
                message.success(res.data.message || "Category updated successfully.");
                setIsModalVisible(false);
                fetchCategories(pagination.current, pagination.pageSize);
                editForm.resetFields();
            } else throw new Error(res.data.message);
        } catch (err) {
            message.error(err?.response?.data?.message || "Failed to update category.");
        } finally { setLoading(false); }
    };

    /* ─── toggle ─────────────────────────────────────────────── */
    const handleToggle = async (record, checked) => {
        try {
            const res = await api.post('/settings/maintaincategory/toggle.php', {
                mc_id:     record.mcId,
                mc_active: checked ? 1 : 0,
            });
            if (res.data.status === 'success') {
                message.success(res.data.message);
                fetchCategories(pagination.current, pagination.pageSize);
            } else throw new Error(res.data.message);
        } catch (err) {
            message.error(err?.response?.data?.message || "Failed to update status.");
        }
    };

    /* ─── columns ────────────────────────────────────────────── */
    const columns = [
        {
            title: '#', key: 'index', width: 60,
            render: (_, __, idx) => (pagination.current - 1) * pagination.pageSize + idx + 1,
        },
        {
            title: 'Category Name', dataIndex: 'mcName', key: 'mcName',
            sorter: (a, b) => a.mcName.localeCompare(b.mcName),
            ...getColumnSearchProps('mcName'),
        },
        {
            title: 'Status', dataIndex: 'mcActive', key: 'mcActive',
            align: 'center', width: 100,
            render: (val) => val == 1
                ? <Tooltip title="Active"><Space size={4}><CheckCircleFilled style={{ color: 'green', fontSize: 16 }} /><Typography.Text style={{ color: 'green' }}>Active</Typography.Text></Space></Tooltip>
                : <Tooltip title="Inactive"><Space size={4}><CloseCircleFilled style={{ color: 'red', fontSize: 16 }} /><Typography.Text style={{ color: 'red' }}>Inactive</Typography.Text></Space></Tooltip>,
        },
        {
            title: 'Action', key: 'action', align: 'center', fixed: 'right', width: 120,
            render: (_, record) => (
                <Space>
                    <Tooltip title="Edit Category">
                        <Button type="text" icon={<EditOutlined />} onClick={() => openEdit(record)} />
                    </Tooltip>
                    <Tooltip title={record.mcActive == 1 ? "Deactivate" : "Activate"}>
                        <Switch
                            size="small"
                            checked={record.mcActive == 1}
                            onChange={(checked) => handleToggle(record, checked)}
                        />
                    </Tooltip>
                </Space>
            ),
        },
    ];

    /* ─── render ─────────────────────────────────────────────── */
    return (
        <Layout className="fluid-page">
            <Headerdiv />

            <div className="fluid-page-header">
                <div className="floating-shape shape-1"></div>
                <div className="floating-shape shape-2"></div>
                <Row align="bottom" justify="space-between">
                    <Col>
                                                    <Breadcrumb
                                className="fluid-breadcrumb"
                                items={[
                                    { href: '/dash', title: <HomeOutlined /> },
                                    { title: 'Settings' },
                                    { title: 'Maintenance Categories' },
                                ]}
                            />
                        <h1 className="fluid-page-title">
                            <ToolOutlined /> Maintenance Categories
                        </h1>
                    </Col>
                    <Col xs={0} md={12} style={{ textAlign: 'right' }}>
                        <p className="fluid-page-subtitle">Manage maintenance task types (e.g. Watering, Fertilizing, Pruning)</p>
                    </Col>
                </Row>
            </div>

            <Content className="fluid-content">
                {/* Table Card */}
                <div className="fluid-card">
                    <div className="fluid-card-header">
                        <h3 className="fluid-card-title">List of Maintenance Categories</h3>
                        <Space>
                            <Typography.Text type="secondary">{pagination.total} records</Typography.Text>
                            <Button type="primary" icon={<PlusOutlined />} onClick={() => setIsAddModalVisible(true)}>
                                Add New Maintenance Category
                            </Button>
                        </Space>
                    </div>
                    <div className="fluid-table">
                        <Table
                            size="small"
                            columns={columns}
                            dataSource={categories}
                            rowKey="mcId"
                            pagination={{
                                current:  pagination.current,
                                pageSize: pagination.pageSize,
                                total:    pagination.total,
                                showSizeChanger: true,
                                showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items`,
                            }}
                            onChange={handleTableChange}
                            loading={loading}
                            scroll={{ x: 500 }}
                        />
                    </div>
                </div>
            </Content>

            <Footerdiv />

            {/* Add Modal */}
            <Modal
                title="Add New Maintenance Category"
                open={isAddModalVisible}
                onCancel={() => { setIsAddModalVisible(false); addForm.resetFields(); }}
                footer={null}
                width={600}
                destroyOnClose
            >
                <Form form={addForm} layout="vertical" onFinish={handleAdd}>
                    <Form.Item
                        label="Category Name" name="mcName"
                        rules={[
                            { required: true, message: 'Please enter a category name' },
                            { max: 100, message: 'Max 100 characters' },
                        ]}
                    >
                        <Input placeholder="e.g. Watering, Fertilizing, Pruning, Pest Control" />
                    </Form.Item>
                    <Row justify="end">
                        <Form.Item style={{ marginBottom: 0 }}>
                            <Space>
                                <Button danger onClick={() => { setIsAddModalVisible(false); addForm.resetFields(); }}>Cancel</Button>
                                <Button type="primary" htmlType="submit" icon={<PlusOutlined />} loading={loading}>Add Category</Button>
                            </Space>
                        </Form.Item>
                    </Row>
                </Form>
            </Modal>

            {/* Edit Modal */}
            <Modal
                title="Edit Maintenance Category"
                open={isModalVisible}
                onCancel={() => { setIsModalVisible(false); editForm.resetFields(); }}
                footer={null}
            >
                <Form form={editForm} onFinish={handleEdit} layout="vertical">
                    <Form.Item
                        label="Category Name" name="mcName"
                        rules={[
                            { required: true, message: 'Please enter a category name' },
                            { max: 100, message: 'Max 100 characters' },
                        ]}
                    >
                        <Input placeholder="e.g. Watering, Fertilizing, Pruning" />
                    </Form.Item>
                    <Form.Item>
                        <Space>
                            <Button type="primary" htmlType="submit" loading={loading}>Save Changes</Button>
                            <Button danger onClick={() => { setIsModalVisible(false); editForm.resetFields(); }}>Cancel</Button>
                        </Space>
                    </Form.Item>
                </Form>
            </Modal>
        </Layout>
    );
};

export default ManageMaintainCategory;
